import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { IConversationEnriched, IMessage } from '@eagerdog/interfaces';
import { messageService } from '../services/message.service';
import { helperService } from '../services/helper.service';

interface IMessagesContext {
  /** Total unread message count across all conversations */
  unreadCount: number;
  /** Whether the Messages panel is open */
  isOpen: boolean;
  /** The currently selected conversation id (null when panel is closed or showing list) */
  activeConversationId: string | null;
  /**
   * Pending recipient for a brand-new conversation (before first message is sent).
   * Set when "Send Message" is clicked but no conversation exists yet.
   */
  pendingRecipientDogId: string | null;
  /** True when pendingRecipientDogId is actually a user _id (not a dog_id). */
  pendingRecipientIsUserId: boolean;
  /** Open the panel without selecting a specific conversation */
  openMessages: () => void;
  /** Close the panel */
  closeMessages: () => void;
  /** Open the panel and navigate to a specific conversation */
  openConversation: (conversationId: string) => void;
  /**
   * Called from the Results tab "Send Message" menu item.
   * Looks up an existing conversation, or prepares a pending new one. Always opens the panel.
   * Pass either dogId (Results tab) or recipientId (Contacts tab / direct user lookup).
   * @param recipientName - display name shown in the panel header before the first message is sent
   */
  initiateConversation: (dogIdOrRecipientId: string, recipientName?: string, isRecipientId?: boolean) => Promise<void>;
  /** Display name of the pending recipient (before the first message is sent) */
  pendingRecipientName: string | null;
  /** Notify context that a conversation was read (clears unread for it) */
  refreshUnreadCount: () => Promise<void>;

  /** In-memory conversations list (kept in sync via socket) */
  conversations: IConversationEnriched[];
  setConversations: React.Dispatch<React.SetStateAction<IConversationEnriched[]>>;
}

const MessagesContext = createContext<IMessagesContext>({
  unreadCount: 0,
  isOpen: false,
  activeConversationId: null,
  pendingRecipientDogId: null,
  pendingRecipientIsUserId: false,
  pendingRecipientName: null,
  openMessages: () => {},
  closeMessages: () => {},
  openConversation: () => {},
  initiateConversation: async () => {},
  refreshUnreadCount: async () => {},
  conversations: [],
  setConversations: () => {},
});

export const useMessages = () => useContext(MessagesContext);

export const MessagesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [pendingRecipientDogId, setPendingRecipientDogId] = useState<string | null>(null);
  const [pendingRecipientIsUserId, setPendingRecipientIsUserId] = useState(false);
  const [pendingRecipientName, setPendingRecipientName] = useState<string | null>(null);
  const [conversations, setConversations] = useState<IConversationEnriched[]>([]);

  const isLoggedIn = helperService.isLoggedIn();
  const [searchParams, setSearchParams] = useSearchParams();

  // On load: if the URL contains ?open_conversation=<id> (e.g. clicked from an email),
  // open the Messages panel to that conversation and strip the param from the URL.
  useEffect(() => {
    if (!isLoggedIn) return;
    const convId = searchParams.get('open_conversation');
    if (!convId) return;
    setIsOpen(true);
    setActiveConversationId(convId);
    setSearchParams((prev) => {
      prev.delete('open_conversation');
      return prev;
    }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount

  // Track whether we've set up socket listeners so we don't duplicate them
  const socketListenersActive = useRef(false);
  const cleanupFns = useRef<Array<() => void>>([]);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const { count } = await messageService.getUnreadCount();
      setUnreadCount(count);
    } catch {
      // silently ignore
    }
  }, []);

  // Connect socket and wire listeners when user is logged in
  useEffect(() => {
    if (!isLoggedIn || socketListenersActive.current) return;

    messageService.connectSocket();
    socketListenersActive.current = true;

    // New message received
    const offNewMessage = messageService.onNewMessage((message, conversation) => {
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c._id === conversation._id);
        if (idx === -1) return [conversation, ...prev];
        const updated = [...prev];
        updated[idx] = conversation;
        return updated;
      });
    });

    // Unread count pushed from server
    const offUnreadCount = messageService.onUnreadCount((count) => {
      setUnreadCount(count);
    });

    // Re-sync count whenever the socket reconnects (server restart / network blip)
    const offConnect = messageService.onConnect(() => {
      refreshUnreadCount();
    });

    // Re-sync count when the user returns to this tab
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshUnreadCount();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    cleanupFns.current = [offNewMessage, offUnreadCount, offConnect];

    // Initial unread count
    refreshUnreadCount();

    return () => {
      cleanupFns.current.forEach((fn) => fn());
      cleanupFns.current = [];
      socketListenersActive.current = false;
      document.removeEventListener('visibilitychange', handleVisibility);
      messageService.disconnectSocket();
    };
  }, [isLoggedIn, refreshUnreadCount]);

  const openMessages = useCallback(() => {
    setIsOpen(true);
    setActiveConversationId(null);
    setPendingRecipientDogId(null);
    setPendingRecipientName(null);
  }, []);

  const closeMessages = useCallback(() => {
    setIsOpen(false);
    setActiveConversationId(null);
    setPendingRecipientDogId(null);
    setPendingRecipientName(null);
  }, []);

  const openConversation = useCallback((conversationId: string) => {
    setActiveConversationId(conversationId);
    setPendingRecipientDogId(null);
    setPendingRecipientName(null);
    setIsOpen(true);
  }, []);

  const initiateConversation = useCallback(async (
    dogIdOrRecipientId: string,
    recipientName?: string,
    isRecipientId = false,
  ) => {
    setIsOpen(true);
    try {
      const existing = isRecipientId
        ? await messageService.findConversation(dogIdOrRecipientId, undefined)
        : await messageService.findConversation(undefined, dogIdOrRecipientId);
      if (existing) {
        setActiveConversationId(existing._id);
        setPendingRecipientDogId(null);
        setPendingRecipientIsUserId(false);
        setPendingRecipientName(null);
      } else {
        setActiveConversationId(null);
        setPendingRecipientDogId(dogIdOrRecipientId);
        setPendingRecipientIsUserId(isRecipientId);
        setPendingRecipientName(recipientName ?? null);
      }
    } catch {
      setActiveConversationId(null);
      setPendingRecipientDogId(dogIdOrRecipientId);
      setPendingRecipientIsUserId(isRecipientId);
      setPendingRecipientName(recipientName ?? null);
    }
  }, []);

  return (
    <MessagesContext.Provider
      value={{
        unreadCount,
        isOpen,
        activeConversationId,
        pendingRecipientDogId,
        pendingRecipientIsUserId,
        pendingRecipientName,
        openMessages,
        closeMessages,
        openConversation,
        initiateConversation,
        refreshUnreadCount,
        conversations,
        setConversations,
      }}
    >
      {children}
    </MessagesContext.Provider>
  );
};
